import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
} from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router'

import { RegistrationContainer } from '@/containers'
import { UserFormData } from '@/entities/User/User'
import { trans } from '@/helpers/trans'
import { setApiToken, setRefreshToken } from '@/redux/authentication/authenticationActions'
import { Reducers } from '@/redux/reducers'
import { getAccessTokenWithRegistrationToken, submitRegistration } from '@/redux/registration/registrationActions'
import { reset } from '@/redux/registration/registrationReducer'
import { useTypedDispatch, useTypedSelector } from '@/redux/store'
import { setToast } from '@/redux/toasts/toastsReducer'
import { getProfileOfCurrentUser } from '@/redux/user/userActions'
import { transformLegacyUserResourceToPacoUser } from '@/services/UserService/transformLegacyUserToPacoUser'
import { ToastType } from '@/types'

export const ConnectedRegistration: FC = (): ReactElement => {
    const dispatch = useTypedDispatch()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const userId = searchParams.get('uuid')
    const confirmId = searchParams.get('confirm')

    const { currentUser: legacyCurrentUser, loading: isUserLoading } = useSelector((state: Reducers) => state.userReducer)
    const {
        isLoading: isRegistrationLoading,
        isSuccessful,
        accessToken,
        error,
    } = useTypedSelector(state => state.pacoRegistrationReducer)

    const currentUser = useMemo(() => (
        legacyCurrentUser ? transformLegacyUserResourceToPacoUser(legacyCurrentUser) : undefined
    ), [legacyCurrentUser])
    const isLoading = isUserLoading || isRegistrationLoading

    const handleSubmitRegistration = (formData: UserFormData): void => {
        if (currentUser) {
            dispatch(submitRegistration(formData, currentUser.id, currentUser.person.id))
        }
    }

    const handleFailedRegistration = (): void => {
        dispatch(setToast({ text: trans('pages.registration.registrationError'), type: ToastType.fail }))
    }

    useEffect((): () => void => () => {
        dispatch(reset())
        dispatch(getProfileOfCurrentUser())
    }, [])

    useEffect((): void => {
        if (userId && confirmId) {
            dispatch(getAccessTokenWithRegistrationToken(userId, confirmId))
        }
    }, [userId, confirmId])

    useEffect((): void => {
        if (accessToken) {
            dispatch(setApiToken(accessToken.token))
            dispatch(setRefreshToken(accessToken.refresh_token))
            dispatch(getProfileOfCurrentUser())
        }
    }, [accessToken])

    useEffect((): void => {
        if (error) {
            handleFailedRegistration()
        }
    }, [error])

    useEffect((): void => {
        if (isSuccessful) {
            dispatch(setToast({
                text: trans('pages.changePasswordPincode.changeSuccess'),
                type: ToastType.pass,
            }))
            navigate('/')
        }
    }, [isSuccessful])

    return (
        <RegistrationContainer
            isLoading={isLoading}
            currentUser={currentUser}
            onSubmitRegistration={handleSubmitRegistration}
        />
    )
}
